// @ts-ignore
import { Autocomplete, DataGrid, textFieldVariants, Tooltip } from '@ghs/components';
import { Cancel, DeleteForever, Edit, Save } from '@mui/icons-material';
import { Box, IconButton, Stack } from '@mui/material';
import { gridClasses, GridRowEditStopReasons, GridRowModes } from '@mui/x-data-grid-pro';
import { useUnit } from 'effector-react';
import { EditableStringCell } from '../../../components/CampaignVersionsDialog/EditableStringCell';
import { DataGridStylesWithoutSelectionOverride } from '../../../util/DataGridStyles';
import { $$adminCustomersPage } from './model';
import { isEmpty } from 'lodash';

/**
 * AdminCustomersTable
 *
 * @returns {React.ReactNode} - JSX Element
 */
export default function AdminCustomersTable() {
  const dataGridProps = $$adminCustomersPage.customers.$$table.useDataGrid();

  const filterValue = useUnit($$adminCustomersPage.customers.$filter);
  const highlightedRows = useUnit($$adminCustomersPage.customers.$$table.$highlightedRows);
  const rowModesModel = useUnit($$adminCustomersPage.customers.$$table.$rowModesModel);
  const errors = useUnit($$adminCustomersPage.customers.$$table.$errors);
  const dataRows = useUnit($$adminCustomersPage.customers.$$table.$data);
  const unsavedChanges = useUnit($$adminCustomersPage.customers.$$table.$unsavedChanges);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut || params.reason === GridRowEditStopReasons.enterKeyDown) {
      event.defaultMuiPrevented = true;
    }
    if (params.reason === GridRowEditStopReasons.enterKeyDown && !errors.some(e => e.id === params.id)) {
      $$adminCustomersPage.customers.saveClicked(params.id);
    }
  };

  /** @type {import('@mui/x-data-grid-pro').GridColDef<Reseller.Customer, any, any>[]} */
  const customerTableColumns = [
    {
      field: 'name',
      headerName: 'Customer',
      editable: true,
      minWidth: 160,
      flex: 1,
      renderEditCell: params => <EditableStringCell {...params} $$table={$$adminCustomersPage.customers.$$table} />
    },
    {
      field: 'actions',
      type: 'actions',
      minWidth: 100,
      headerName: 'Actions',
      headerAlign: 'center',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const customer = dataGridProps.rows.find(c => c.id === id);
        const isCellWithError = errors.some(e => e.id === id);
        const isRowHasUnsavedChanges = unsavedChanges.find(r => r.id === id);
        if (!customer) {
          return [];
        }
        return isInEditMode
          ? [
              <Tooltip key={`customer-save-button-${id}`} title={!isCellWithError && (isRowHasUnsavedChanges ? 'Save' : 'No changes to save')} placement="bottom" arrow>
                <span>
                  <IconButton key={`customer-save-button-${id}`} disabled={isCellWithError || !isRowHasUnsavedChanges} onClick={() => $$adminCustomersPage.customers.saveClicked(id)}>
                    <Save fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>,
              <IconButton key={`customer-cancel-button-${id}`} onClick={() => $$adminCustomersPage.customers.cancelClicked(id)}>
                <Cancel fontSize="small" />
              </IconButton>
            ]
          : [
              <IconButton key={`customer-edit-button-${id}`} onClick={() => $$adminCustomersPage.customers.editClicked(id)}>
                <Edit fontSize="small" color="primary" />
              </IconButton>,
              <Tooltip key={`customer-delete-button-${id}`} title="Delete" placement="bottom" arrow onClick={() => $$adminCustomersPage.customers.deleteClicked(id)}>
                <IconButton key={`customer-delete-button-${id}`} sx={{ '&.Mui-disabled': { pointerEvents: 'auto' } }}>
                  <DeleteForever fontSize="small" color="warning" />
                </IconButton>
              </Tooltip>
            ];
      }
    }
  ];

  const rows = dataGridProps.rows.filter(row => (filterValue !== null ? row.id === filterValue : true));

  return (
    <Stack sx={{ flex: 1, gap: 2 }}>
      <Autocomplete
        data-testid="customers-batch-actions-autocomplete"
        id="customers-batch-actions-autocomplete"
        label="Search Customer"
        placeholder="Select"
        options={dataGridProps.rows.map(row => ({ id: row.id, label: row.name }))}
        onChange={(_, value) => $$adminCustomersPage.customers.filterSelected(value?.id ?? null)}
        variant={textFieldVariants.OUTLINED}
        value={dataRows.find(row => row.id === filterValue)?.name || ''}
        getOptionKey={option => option.id}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: isEmpty(rows) ? 250 : undefined
        }}
      >
        <DataGrid
          data-testid="customers-datagrid"
          {...dataGridProps}
          rows={rows}
          columns={customerTableColumns}
          onRowEditStop={handleRowEditStop}
          onRowClick={({ id }) => $$adminCustomersPage.customerSelected(Number(id))}
          initialState={{
            columns: { columnVisibilityModel: { id: false } },
            pagination: { paginationModel: { pageSize: 10 } }
          }}
          pagination
          defaultPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          autosizeOnMount={false} // vitest is failing beause of this prop
          autosizeOptions={{
            includeOutliers: true,
            includeHeaders: true
          }}
          getCellClassName={params =>
            Object.entries({
              [`${gridClasses.cell}--error`]: Boolean(errors.find(e => e.id === params.id && e.field === params.field)),
              'editable-date-cell': rowModesModel[params.row.id]?.mode === 'edit' && params.isEditable === true,
              'editable-date': rowModesModel[params.row.id]?.mode === 'edit' && params.colDef.type === 'date',
              // [`${gridClasses.cell}--edited`]: unsavedChanges.find(r => r.id === params.row.id && r.field === params.field),
              [`${gridClasses.cell}--success`]: highlightedRows.includes(params.row.id)
            })
              .filter(([, value]) => value)
              .map(([key]) => key)
              .slice(0, 1)
              .join(' ')
          }
          sx={DataGridStylesWithoutSelectionOverride}
        />
      </Box>
    </Stack>
  );
}
